import { Accordion, ButtonGroup, Flex, Icon, Tag, Text } from '@chakra-ui/react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardWrapperLayout from 'layouts/dashboard-wrapper';
import { isEmpty } from 'common/utils/isEmpty';
import PlanInfoCard from 'components/plans/plan-info-card';
import { useEffect, useMemo, useState } from 'react';
import {
  useGetSinglePlanQuery,
  useCreateClaimMutation,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/features/auth/authSlice';
import PaymentSummaryCard from 'components/plans/payment-summary-card';
import { coverageTypes, planPaymentMode } from 'utils/enums';
import ContactCard from 'components/shared/contact-card';
import { PostPurchaseCoverageCard } from 'components/coverages/coverage-card';
import { isWithinDateRange } from 'common/utils/dateRange';
import EmptyPage from 'common/components/empty-page';
import { InfoDetail, MemberInfoCard } from 'components/shared/member-info-card';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import FormatAsCurrency from 'common/utils/formatAsCurrency';
import { FaExclamationCircle } from 'react-icons/fa';
import { leaseTypeEnum } from 'common/utils/enums';
import { leaseCategoryOptions } from 'common/utils/selectOptions';
import Button from 'common/components/button';
import ContactSupportModal from 'components/shared/help-button';

const PlanDetails = () => {
  // HOOKS
  const { planId } = useParams();
  const navigate = useNavigate();
  const { data: singlePlan, isLoading: getSinglePlanLoading } =
    useGetSinglePlanQuery(planId);
  const [createClaim, { data: claimData, isLoading: createClaimLoading }] =
    useCreateClaimMutation();

  // Retrieve user details
  const userDetails = useSelector(selectUser);

  // STATES

  // Check eligibility of coverages
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const cancelAnytime = singlePlan?.coverage?.cancelAnytime;
  const leaveAnytime = singlePlan?.coverage?.leaveAnytime;

  const hasCancelAnytime = !!cancelAnytime?.payment?.stripe?.id;
  const hasLeaveAnytimeOnetime = !!leaveAnytime?.payment?.onetime?.stripe?.id;
  const hasLeaveAnytimeSubscription = !!leaveAnytime?.payment?.subscription?.stripe?.id;
  const hasLeaveAnytimeSplit = !!leaveAnytime?.payment?.split?.subscription?.stripe?.id || !!leaveAnytime?.payment?.split?.onetime?.stripe?.id;

  const hasLeaveAnytime = hasLeaveAnytimeOnetime || hasLeaveAnytimeSubscription || hasLeaveAnytimeSplit;

  const leaveAnytimeEligible = useMemo(
    () => hasLeaveAnytime && isWithinDateRange(leaveAnytime, now),
    [now, hasLeaveAnytime, leaveAnytime]
  );

  const cancelAnytimeEligible = useMemo(
    () => hasCancelAnytime && isWithinDateRange(cancelAnytime, now),
    [now, hasCancelAnytime, cancelAnytime]
  );

  const hasLimitedCoverage = useMemo(
    () => leaveAnytime?.endDate < singlePlan?.lease?.endDate,
    [leaveAnytime, singlePlan]
  );

  const address = singlePlan?.lease?.address;

  const concessions = singlePlan?.lease?.concessions?.repayable
    ? singlePlan?.lease?.concessions?.discounts || 0
    : 0;

  const program = singlePlan?.property?.owner?.program;
  const rates = singlePlan?.property?.rates?.underwriting;

  const isMonthToMonth =
    singlePlan?.lease?.leaseType === leaseTypeEnum.MONTH_TO_MONTH_LEASE;

  // Set up tabs for page state
  // Set up url query params for activeTab
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(activeTabFromUrl || 'details');

  // keep URL in sync with active tab
  useEffect(() => {
    // merge only the 'tab' param, preserving others, avoid flicker on identical value
    if (searchParams.get('tab') !== activeTab) {
      const params = new URLSearchParams(searchParams);
      params.set('tab', activeTab);
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const tabs = [
    {
      title: 'Details',
      selected: activeTab === 'details',
      onClick: () => {
        setActiveTab('details');
      }
    },
    {
      title: 'Coverage',
      selected: activeTab === 'coverage',
      onClick: () => {
        setActiveTab('coverage');
      }
    }
  ]

  const buttons = (
    <ButtonGroup>
      {leaveAnytimeEligible || cancelAnytimeEligible ? (
        <Button
          disabled={!leaveAnytimeEligible && !cancelAnytimeEligible}
          onClick={() => {
            createClaim({
              value: { planID: planId },
            })
              .unwrap()
              .then((x) => {
                navigate(`/dashboard/claims/submit/${x?.data?.id}`);
              });
          }}
          title='Submit a Claim'
          wFull={false}
        />
      ) : null}
      <ContactSupportModal
        buttonProps={{
          title: 'Get Help',
          theme: 'secondary',
        }}
        modalHeader='Contact Support'
      />
    </ButtonGroup>
  )

  return (
    <DashboardWrapperLayout 
      title={`Plan Details`} 
      buttons={buttons}
      tabs={tabs}
    >
      <Flex className='w-full flex-col gap-12 bg-white'>
        {/* plan card and payment summary */}
        {getSinglePlanLoading ? (
          <EmptyPage
            title='Loading Your Plan'
            subtitle='Please wait while we load your plan details.'
            isLoading={true}
          />
        ) : isEmpty(singlePlan) ? (
          <EmptyPage
            title='No Plan Found'
            subtitle='We could not find any plan details for you.'
          />
        ) : (
          <>
            {/* plan card and info */}
            {activeTab === 'details' ? (
              <>
              <PlanInfoCard plan={singlePlan} member={userDetails} />
              <MemberInfoCard
                title={
                  <>
                    {address?.streetAddress1}
                    {address?.streetAddress2
                      ? `, ${address?.streetAddress2}, `
                      : ', '}
                    {address?.city}, {address?.state || address?.province}{' '}
                    {address?.zipCode || address?.postalCode}
                  </>
                }
                subtitle={
                  <div className='inline-flex gap-2'>
                    <Text>
                      {isMonthToMonth
                        ? `Month-to-month from ${formatDateToHumanDate(
                            leaveAnytime?.startDate
                          )}`
                        : `${formatDateToHumanDate(
                            leaveAnytime?.startDate
                          )} - ${formatDateToHumanDate(leaveAnytime?.endDate)}`}
                    </Text>
                    {singlePlan?.lease?.category && (
                      <Tag size='sm' colorScheme='blue'>
                        {
                          leaseCategoryOptions.find(
                            (option) =>
                              option.value === singlePlan?.lease?.category
                          )?.label
                        }
                      </Tag>
                    )}
                  </div>
                }
                buttonText={singlePlan?.lease?.document && 'View Lease Agreement'}
                onClick={singlePlan?.lease?.document && (() => {
                  window.open(singlePlan?.lease?.document, '_blank');
                })}
              />
              <PaymentSummaryCard
                isLoading={false}
                isComplete={true}
                plan={singlePlan}
              />
            </>
            ) : activeTab === 'coverage' ? (
              hasCancelAnytime || hasLeaveAnytime ? (
                <Flex className='flex-col gap-6'>
                  {/* coverages */}
                  {hasCancelAnytime ? (
                    <PostPurchaseCoverageCard
                      coverageType={coverageTypes.CANCEL_ANYTIME}
                    >
                      <Accordion>
                      <InfoDetail
                        title='Coverage Period'
                        subtitle={`${formatDateToHumanDate(
                          cancelAnytime?.startDate
                        )} - ${formatDateToHumanDate(cancelAnytime?.endDate)}`}
                      >
                        <Text>
                          Your coverage period starts on{' '}
                          {formatDateToHumanDate(cancelAnytime?.startDate)} and
                          ends on {formatDateToHumanDate(cancelAnytime?.endDate)}.
                          You are eligible to cancel your lease during
                          this period and receive benefits.
                        </Text>
                      </InfoDetail>
                      <InfoDetail
                        title='Deductible'
                        subtitle={
                          <span>
                            {FormatAsCurrency(cancelAnytime?.deductible || 0, 'USD')}<span className='text-gray-500'> (due at time of claim)</span>
                          </span>
                        }
                      >
                        <Text>
                          Your coverage includes a deductible in the amount of{' '}
                          {FormatAsCurrency(cancelAnytime?.deductible || 0, 'USD')}. This amount must be paid at the time of a claim before ReLease begins covering the rest of your early termination fees.
                        </Text>
                      </InfoDetail>
                      <InfoDetail
                        title='Notice Period'
                        subtitle={
                          <span>
                            {cancelAnytime?.noticePeriod || 3} days<span className='text-gray-500'> (before move-out date)</span>
                          </span>
                        }
                      >
                        <Text>
                          You must provide at least {cancelAnytime?.noticePeriod || 3} days notice before your move-out date. 
                          This means you need to submit your claim at least {cancelAnytime?.noticePeriod || 3} days before 
                          the earliest date you want to move out.
                        </Text>
                      </InfoDetail>
                      {concessions > 0 ? (
                        <InfoDetail
                          title='Non-Covered Expenses'
                          subtitle={FormatAsCurrency(concessions, 'USD')}
                        >
                          <Text>
                            ReLease does not cover the{' '}
                            {FormatAsCurrency(concessions, 'USD')} in concessions
                            you received from your landlord as part of this lease
                            agreement.
                          </Text>
  
                          <Text>
                            ReLease does not cover any concessions or discounts
                            you receive from your landlord. Any non-covered
                            expenses will need to be paid out of pocket prior to
                            receiving benefits. ReLease does not cover or change
                            any terms related to cleaning fees, pet fees, or other
                            fees that are not part of your lease agreement.
                          </Text>
                        </InfoDetail>
                      ) : null}
                      </Accordion>
                    </PostPurchaseCoverageCard>
                  ) : null}
                  {hasLeaveAnytime && (
                    <PostPurchaseCoverageCard
                      coverageType={coverageTypes.LEAVE_ANYTIME}
                    >
                      <Accordion>
                      <InfoDetail
                        title='Coverage Period'
                        subtitle={
                          isMonthToMonth
                            ? `Month-to-month from ${formatDateToHumanDate(
                                leaveAnytime?.startDate
                              )}`
                            : `${formatDateToHumanDate(
                                leaveAnytime?.startDate
                              )} - ${formatDateToHumanDate(
                                leaveAnytime?.endDate
                              )}`
                        }
                      >
                        {hasLimitedCoverage ? (
                          <Tag colorScheme='yellow' className='py-1 w-fit'>
                            <Icon as={FaExclamationCircle} className='mr-2' />
                            Your coverage is limited and doesn’t extend through
                            your entire lease agreement. Please contact us for
                            more information.
                          </Tag>
                        ) : null}
                        {isMonthToMonth ? (
                          <Text>
                            Your coverage period starts on{' '}
                            {formatDateToHumanDate(leaveAnytime?.startDate)} and
                            renews each month until the end of your lease
                            agreement. You are eligible to leave your lease during this period and receive benefits.
                          </Text>
                        ) : (
                          <Text>
                            Your coverage period starts on{' '}
                            {formatDateToHumanDate(leaveAnytime?.startDate)} and
                            ends on {formatDateToHumanDate(leaveAnytime?.endDate)}
                            . You are eligible to leave your lease during this period and receive benefits.
                          </Text>
                        )}
                      </InfoDetail>
                      <InfoDetail
                        title='Deductible'
                        subtitle={
                          <span>
                            {FormatAsCurrency(leaveAnytime?.deductible || 0, 'USD')}<span className='text-gray-500'> (due at time of claim)</span>
                          </span>
                        }
                      >
                        <Text>
                          Your coverage includes a deductible in the amount of{' '}
                          {FormatAsCurrency(leaveAnytime?.deductible || 0, 'USD')}. This amount must be paid at the time of a claim before ReLease begins covering the rest of your early termination fees.
                        </Text>
                      </InfoDetail>
                      <InfoDetail
                        title='Notice Period'
                        subtitle={
                          <span>
                            {leaveAnytime?.noticePeriod || 3} days<span className='text-gray-500'> (before move-out date)</span>
                          </span>
                        }
                      >
                        <Text>
                          You must provide at least {leaveAnytime?.noticePeriod || 3} days notice before your move-out date. 
                          This means you need to submit your claim at least {leaveAnytime?.noticePeriod || 3} days before 
                          the earliest date you want to move out.
                        </Text>
                      </InfoDetail>
                      {concessions > 0 ? (
                        <InfoDetail
                          title='Non-Covered Expenses'
                          subtitle={FormatAsCurrency(concessions, 'USD')}
                        >
                          <Text>
                            ReLease does not cover the{' '}
                            {FormatAsCurrency(concessions, 'USD')} in concessions
                            you received from your landlord as part of this lease
                            agreement.
                          </Text>
  
                          <Text>
                            ReLease does not cover any concessions or discounts
                            you receive from your landlord. Any non-covered
                            expenses will need to be paid out of pocket prior to
                            receiving benefits. ReLease does not cover or change
                            any terms related to cleaning fees, pet fees, or other
                            fees that are not part of your lease agreement.
                          </Text>
                        </InfoDetail>
                      ) : null}
                      </Accordion>
                    </PostPurchaseCoverageCard>
                  )}
                </Flex>
              ) : (
                <EmptyPage
                  title='No Coverage'
                  subtitle='Your plan does not include any coverage.'
                />
              )
            ) : null}
          </>
        )}

        {/* Contact Us Card */}
        <ContactCard title='Contact Us'>
          Questions about your plan or with claims? Contact us by email, phone,
          or text and we can help you answer any questions you may have about
          purchasing a plan, making claims, and more.
        </ContactCard>
      </Flex>
    </DashboardWrapperLayout>
  );
};

export default PlanDetails;
